import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Save, Shield, Trash2, ShieldCheck, Crown, Users, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import RolePermissionPreview from './RolePermissionPreview';
import RefusalLogPanel from './RefusalLogPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE_COLORS = {
  superadmin: 'bg-red-100 text-red-800 border-red-300',
  admin: 'bg-blue-100 text-blue-800 border-blue-300',
  manager: 'bg-purple-100 text-purple-800 border-purple-300',
  accounts: 'bg-green-100 text-green-800 border-green-300',
  employee: 'bg-amber-100 text-amber-800 border-amber-300',
  auditor: 'bg-slate-100 text-slate-800 border-slate-300',
  user: 'bg-gray-100 text-gray-800 border-gray-300',
};

const ACTION_LABELS = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  sign: 'Sign',
  export: 'Export',
  manage: 'Manage',
};

const DATA_SCOPE_OPTIONS = [
  { value: 'all', label: i18n.t('auto.lbl.allRecords'), hint: i18n.t('auto.lbl.seesEveryRecordSystemWideBack') },
  { value: 'team', label: i18n.t('auto.lbl.teamOnly'), hint: i18n.t('auto.lbl.seesOnlyProjectsTimesheetsTheUser') },
  { value: 'own', label: i18n.t('auto.lbl.ownRecords'), hint: i18n.t('auto.lbl.seesOnlyRecordsThatReferenceThis') },
];

const RolePermissionsMatrix = () => {
  const { t } = useTranslation();
  const { isSuperadmin, reload: reloadMyPerms, viewAsRole, setViewAsRole, realIsAdmin } = usePermissions();
  const [catalog, setCatalog] = useState(null);
  const [roles, setRoles] = useState([]);
  const [draft, setDraft] = useState({}); // { role: Set<perm_key> }
  const [scopeDraft, setScopeDraft] = useState({}); // { role: "all" | "team" | "own" }
  const [dirty, setDirty] = useState({}); // { role: true }
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleLabel, setNewRoleLabel] = useState('');

  // Superadmin assignment
  const [superadmins, setSuperadmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [supDialogOpen, setSupDialogOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, rolesRes] = await Promise.all([
        axios.get(`${API}/permissions/catalog`),
        axios.get(`${API}/role-permissions`),
      ]);
      setCatalog(catRes.data);
      setRoles(rolesRes.data);
      const d = {};
      const s = {};
      rolesRes.data.forEach((r) => {
        d[r.role] = new Set(r.permissions || []);
        s[r.role] = r.data_scope || 'all';
      });
      setDraft(d);
      setScopeDraft(s);
      setDirty({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load permissions matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuperadminData = useCallback(async () => {
    try {
      const [sa, users] = await Promise.all([
        axios.get(`${API}/superadmins`),
        axios.get(`${API}/users`),
      ]);
      setSuperadmins(sa.data || []);
      setAllUsers(users.data || []);
    } catch (e) {
      // Non-fatal — only superadmins can read these
    }
  }, []);

  useEffect(() => {
    fetchAll();
    if (isSuperadmin) fetchSuperadminData();
  }, [fetchAll, fetchSuperadminData, isSuperadmin]);

  /** Tick a permission straight from the refusal log. */
  const grantFromRefusal = (role, key) => {
    if (!role || role === 'superadmin') return;
    if (draft[role]?.has(key)) {
      toast.info(t('auto.set.alreadyGrantedPressSaveIfThe'));
      return;
    }
    setDraft((d) => {
      const next = { ...d };
      const set = new Set(next[role] || []);
      set.add(key);
      next[role] = set;
      return next;
    });
    setDirty((p) => ({ ...p, [role]: true }));
    toast.success(`Ticked ${key} for ${role} — press “Save all changes” to apply`);
  };

  /** Mark a role dirty only while its draft really differs from what's saved. */
  const refreshDirty = (role, perms, scope) => {
    const saved = roles.find((r) => r.role === role);
    const savedPerms = new Set(saved?.permissions || []);
    const samePerms = savedPerms.size === perms.size
      && Array.from(perms).every((k) => savedPerms.has(k));
    const sameScope = (saved?.data_scope || 'all') === (scope || 'all');
    setDirty((p) => {
      const next = { ...p };
      if (samePerms && sameScope) delete next[role];
      else next[role] = true;
      return next;
    });
  };

  const togglePerm = (role, key) => {
    if (role === 'superadmin') return; // immutable
    const set = new Set(draft[role] || []);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    setDraft((d) => ({ ...d, [role]: set }));
    refreshDirty(role, set, scopeDraft[role]);
  };

  const toggleAllForModule = (role, module) => {
    if (role === 'superadmin') return;
    const keys = module.actions.map((a) => `${module.key}.${a}`);
    const set = new Set(draft[role] || []);
    const allOn = keys.every((k) => set.has(k));
    keys.forEach((k) => (allOn ? set.delete(k) : set.add(k)));
    setDraft((d) => ({ ...d, [role]: set }));
    refreshDirty(role, set, scopeDraft[role]);
  };

  const changeScope = (role, value) => {
    if (role === 'superadmin') return;
    setScopeDraft((s) => ({ ...s, [role]: value }));
    refreshDirty(role, new Set(draft[role] || []), value);
  };

  /** Persist one role without touching the drafts of the others. */
  const putRole = async (role) => {
    const perms = Array.from(draft[role] || []);
    const scope = scopeDraft[role] || 'all';
    await axios.put(`${API}/role-permissions/${role}`, {
      permissions: perms,
      data_scope: scope,
    });
    setRoles((rs) => rs.map((r) => (
      r.role === role ? { ...r, permissions: perms, data_scope: scope } : r
    )));
    setDirty((p) => {
      const c = { ...p };
      delete c[role];
      return c;
    });
  };

  const saveRole = async (role) => {
    setSaving((s) => ({ ...s, [role]: true }));
    try {
      await putRole(role);
      toast.success(`Saved ${role} permissions`);
      reloadMyPerms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving((s) => {
        const c = { ...s };
        delete c[role];
        return c;
      });
    }
  };

  /** Save every role edited since the last save, in one go. */
  const saveAll = async () => {
    const pending = Object.keys(dirty).filter((r) => r !== 'superadmin');
    if (!pending.length) return;
    setSaving((s) => ({ ...s, __all__: true }));
    const failed = [];
    for (const role of pending) {
      try {
        await putRole(role);
      } catch (e) {
        failed.push(`${role}: ${e.response?.data?.detail || 'save failed'}`);
      }
    }
    setSaving((s) => {
      const c = { ...s };
      delete c.__all__;
      return c;
    });
    if (failed.length) toast.error(`Some roles failed — ${failed.join('; ')}`);
    else toast.success(`Saved ${pending.length} role${pending.length > 1 ? 's' : ''}`);
    reloadMyPerms();
  };

  const createRole = async () => {
    const name = newRoleName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!name) {
      toast.error(t('auto.set.roleNameIsRequired'));
      return;
    }
    try {
      await axios.post(`${API}/roles`, {
        name,
        label: newRoleLabel.trim() || undefined,
        permissions: [],
      });
      toast.success(`Role "${name}" created`);
      setCreateOpen(false);
      setNewRoleName('');
      setNewRoleLabel('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create role');
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete custom role "${role}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/roles/${role}`);
      toast.success(`Role "${role}" deleted`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleSuperadmin = async (userId, current) => {
    try {
      await axios.put(`${API}/users/${userId}/superadmin`, { is_superadmin: !current });
      toast.success(`Superadmin ${current ? 'revoked' : 'granted'}`);
      fetchSuperadminData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading || !catalog) {
    return <div className="text-sm text-gray-400 p-4">{t('auto.set.loadingPermissionsMatrix')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="role-permissions-matrix">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Roles &amp; Permissions Matrix
              </CardTitle>
              <CardDescription>
                Configure module-level access for each role. The frontend uses these to gate
                tabs and actions; backend role checks remain in place as a defence-in-depth layer.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {Object.keys(dirty).filter((r) => r !== 'superadmin').length > 0 && (
                <Button
                  size="sm"
                  onClick={saveAll}
                  disabled={!!saving.__all__}
                  data-testid="save-all-roles-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving.__all__
                    ? 'Saving…'
                    : `Save all changes (${Object.keys(dirty).filter((r) => r !== 'superadmin').length})`}
                </Button>
              )}
              {/* View-as-role picker (admins/superadmins only) — preview
                  the UI through the lens of any role without logging
                  in/out as a test user. */}
              {realIsAdmin && (
                <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                  <Eye className="h-3.5 w-3.5 text-amber-600" />
                  <select
                    value={viewAsRole || ''}
                    onChange={(e) => setViewAsRole(e.target.value || null)}
                    className="bg-transparent text-xs font-medium text-amber-700 focus:outline-none cursor-pointer"
                    data-testid="view-as-role-picker"
                  >
                    <option value="">{t('auto.set.viewAsOwnRole')}</option>
                    {roles.map((r) => (
                      <option key={r.role} value={r.role}>
                        View as: {r.label || r.role}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {isSuperadmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSupDialogOpen(true)}
                  data-testid="manage-superadmins-btn"
                >
                  <Crown className="h-4 w-4 mr-2 text-amber-500" />
                  Superadmins ({superadmins.length})
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setCreateOpen(true)}
                data-testid="create-custom-role-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('auto.set.newRole')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          <div className="overflow-auto w-full max-h-[75vh]">
          <table className="w-full min-w-max border-collapse text-sm" data-testid="permissions-table">
            <thead>
              <tr className="border-b-2 bg-slate-50">
                <th className="text-left p-2 sticky left-0 top-0 bg-slate-50 z-30 min-w-[180px]">
                  {t('auto.set.moduleAction')}
                </th>
                {roles.map((r) => (
                  <th key={r.role} className="p-2 align-bottom min-w-[120px] sticky top-0 bg-slate-50 z-20">
                    <div className="flex flex-col items-center gap-1">
                      <Badge
                        variant="outline"
                        className={`${ROLE_BADGE_COLORS[r.role] || 'bg-gray-100 text-gray-800'} flex items-center gap-1`}
                      >
                        {r.role === 'superadmin' && <Crown className="h-3 w-3" />}
                        {r.label || r.role}
                      </Badge>
                      <div className="flex gap-1">
                        {dirty[r.role] && r.role !== 'superadmin' && (
                          <Button
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => saveRole(r.role)}
                            disabled={saving[r.role]}
                            data-testid={`save-role-${r.role}-btn`}
                          >
                            <Save className="h-3 w-3 mr-1" />
                            {t('auto.set.save')}
                          </Button>
                        )}
                        {!r.is_system && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-red-600"
                            onClick={() => deleteRole(r.role)}
                            data-testid={`delete-role-${r.role}-btn`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Data-scope row — admin chooses the row-set each role sees */}
              <tr className="bg-amber-50 border-b border-amber-200">
                <td className="p-2 sticky left-0 bg-amber-50 z-10 text-xs">
                  <div className="font-semibold text-amber-900">{t('auto.set.dataScope')}</div>
                  <div className="text-[10px] text-amber-700 font-normal">
                    {t('auto.set.whichRecordsThisRoleCanSee')}
                  </div>
                </td>
                {roles.map((r) => {
                  const locked = r.role === 'superadmin';
                  const value = locked ? 'all' : (scopeDraft[r.role] || 'all');
                  const opt = DATA_SCOPE_OPTIONS.find((o) => o.value === value);
                  return (
                    <td key={r.role} className="p-2 text-center">
                      <select
                        value={value}
                        disabled={locked}
                        onChange={(e) => changeScope(r.role, e.target.value)}
                        className="text-xs rounded border border-amber-300 bg-white px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:bg-slate-100 disabled:text-slate-400 cursor-pointer"
                        data-testid={`data-scope-${r.role}`}
                        title={opt?.hint || ''}
                      >
                        {DATA_SCOPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                  );
                })}
              </tr>
              {catalog.modules.map((mod) => (
                <React.Fragment key={mod.key}>
                  <tr className="bg-slate-100 border-t">
                    <td colSpan={roles.length + 1} className="p-2 font-semibold text-slate-700">
                      <span className="sticky left-2 inline-flex items-center">
                        {mod.label}
                        <span className="text-xs text-slate-500 ml-2 font-normal">
                          ({mod.actions.length} action{mod.actions.length > 1 ? 's' : ''})
                        </span>
                      </span>
                    </td>
                  </tr>
                  {mod.actions.map((act) => {
                    const key = `${mod.key}.${act}`;
                    return (
                      <tr key={key} className="border-b hover:bg-slate-50">
                        <td className="p-2 pl-6 sticky left-0 bg-white hover:bg-slate-50 z-10 text-xs text-slate-600">
                          <span className="font-medium text-slate-800">{ACTION_LABELS[act] || act}</span>
                          <span className="text-slate-400 ml-2 font-mono">{key}</span>
                        </td>
                        {roles.map((r) => {
                          const checked = (draft[r.role] || new Set()).has(key);
                          const locked = r.role === 'superadmin';
                          return (
                            <td key={r.role} className="p-2 text-center">
                              <Checkbox
                                checked={locked ? true : checked}
                                disabled={locked}
                                onCheckedChange={() => togglePerm(r.role, key)}
                                data-testid={`perm-${r.role}-${key}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr className="border-b">
                    <td className="p-1 pl-6 sticky left-0 bg-white z-10">
                      <span className="text-[10px] text-slate-400 italic">toggle all in {mod.label}</span>
                    </td>
                    {roles.map((r) => (
                      <td key={r.role} className="p-1 text-center">
                        {r.role !== 'superadmin' && (
                          <button
                            type="button"
                            onClick={() => toggleAllForModule(r.role, mod)}
                            className="text-[10px] text-blue-600 hover:underline"
                            data-testid={`toggle-all-${r.role}-${mod.key}`}
                          >
                            all
                          </button>
                        )}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          <div className="mt-4 px-4 sm:px-0 text-xs text-slate-500 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Backend permission checks are enforced from this same matrix. Changes to several
            roles are kept until you save — use “Save all changes” at the top, or the Save
            button under a single column.
          </div>
          {catalog.admin_only?.length > 0 && (
            <div className="mt-4 mx-4 sm:mx-0 rounded-md border border-slate-200 bg-slate-50 p-3"
              data-testid="admin-only-actions">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <Crown className="h-3.5 w-3.5 text-amber-500" />
                Administrators only — not grantable
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                These actions stay with administrators and superadmins whatever the matrix says,
                because they can destroy or expose the whole dataset, or hand out access itself.
              </p>
              <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
                {catalog.admin_only.map((a) => (
                  <li key={a.action} className="text-[11px] text-slate-600">
                    <span className="font-mono text-slate-400 mr-1">{a.action}</span>
                    {a.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create role dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-role-dialog">
          <DialogHeader>
            <DialogTitle>{t('auto.set.createCustomRole')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600">{t('auto.set.roleIdentifierLowercaseNoSpaces')}</label>
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. site_supervisor"
                data-testid="new-role-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">{t('auto.set.displayLabel')}</label>
              <Input
                value={newRoleLabel}
                onChange={(e) => setNewRoleLabel(e.target.value)}
                placeholder="e.g. Site Supervisor"
                data-testid="new-role-label-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="dialog-cancel-btn">{t('auto.set.cancel')}</Button>
            <Button onClick={createRole} data-testid="confirm-create-role-btn">{t('auto.set.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plain-English summary + what people are being refused */}
      {catalog && roles.length > 0 && (
        <RolePermissionPreview
          catalog={catalog}
          roles={roles.map((r) => ({
            ...r,
            permissions: Array.from(draft[r.role] || r.permissions || []),
            data_scope: scopeDraft[r.role] || r.data_scope,
          }))}
        />
      )}
      <RefusalLogPanel onGrant={grantFromRefusal} />

      {/* Superadmin assignment dialog */}
      <Dialog open={supDialogOpen} onOpenChange={setSupDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="superadmins-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              {t('auto.set.manageSuperadmins')}
            </DialogTitle>
            <CardDescription>
              {t('auto.set.superadminsBypassAllRolePermissionChecks')}
            </CardDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-1">
            {allUsers.length === 0 && (
              <div className="text-sm text-slate-400 p-4">{t('auto.set.noUsersAvailable')}</div>
            )}
            {allUsers
              .sort((a, b) => (a.full_name || a.username).localeCompare(b.full_name || b.username))
              .map((u) => {
                const isSup = !!u.is_superadmin || superadmins.some((s) => s.id === u.id);
                return (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-2 border rounded hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-slate-400" />
                      <div>
                        <div className="font-medium text-sm">{u.full_name || u.username}</div>
                        <div className="text-xs text-slate-500">
                          {u.email} · <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={isSup ? 'destructive' : 'outline'}
                      onClick={() => toggleSuperadmin(u.id, isSup)}
                      data-testid={`toggle-superadmin-${u.username}-btn`}
                    >
                      {isSup ? (<><Trash2 className="h-3 w-3 mr-1" />{t('auto.set.revoke')}</>) : (<><Crown className="h-3 w-3 mr-1" />{t('auto.set.grant')}</>)}
                    </Button>
                  </div>
                );
              })}
          </div>
          <DialogFooter>
            <Button onClick={() => setSupDialogOpen(false)} data-testid="dialog-close-btn">{t('auto.set.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolePermissionsMatrix;
